import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';
import {
  Action,
  ActionFunction0,
  ActionFunction1,
  createAction,
} from 'redux-actions';
import { useSelector } from 'react-redux';
import { createSelector, OutputSelector } from 'reselect';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { isApiError } from '@wix/da-http-client';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import {
  BaseSaveableWidgetState,
  BaseWidgetState,
  NetworkRequestState,
  RequestMeta,
  ErrorDetails,
  FailureAction,
  PuppyApiResponseWrapper,
  SuccessAction,
} from './baseTypes';
import { all, put } from 'redux-saga/effects';
import i18next from 'i18next';

function isWidgetStateDirty<T extends BaseWidgetState<any>>(
  widgetState: T
): boolean {
  return !isEqual(widgetState.currentData, widgetState.initialData);
}

export function mergeWidgetDataChange<T extends BaseWidgetState<any>>(
  widgetState: T | undefined,
  data: Partial<T['initialData']>,
  defaultData: T
): T {
  if (!widgetState) {
    return defaultData;
  }
  const updatedWidgetState = {
    ...widgetState,
    currentData: widgetState.currentData
      ? {
          ...widgetState.currentData,
          ...data,
        }
      : widgetState.initialData,
  };

  return updatedWidgetState;
}

export function mergeSaveableWidgetDataChange<
  T extends BaseSaveableWidgetState<any, any>,
>(
  widgetState: T,
  data: Partial<T['currentData']>,
  defaultWidgetState: T,
  getIsDirty: (widgetState: T) => boolean = isWidgetStateDirty,
  resetErrorStateOnChange: boolean = false
) {
  const updatedWidgetState = mergeWidgetDataChange(
    widgetState,
    data,
    defaultWidgetState
  );
  updatedWidgetState.isDirty = getIsDirty(updatedWidgetState);

  if (resetErrorStateOnChange) {
    updatedWidgetState.saveRequest = {
      errorMessage: undefined,
      errors: {},
      status: NetworkRequestState.IDLE,
    };
  }

  return updatedWidgetState;
}

export const INITIAL_SAVE_REQUEST_META: RequestMeta<any> = {
  status: NetworkRequestState.IDLE,
  errors: {},
};

export function makeSpecificWidgetDataMergeFunction<
  T extends BaseWidgetState<any>,
>(defaultWidgetState: T) {
  return (widgetState: T, data: Partial<T['currentData']>) =>
    mergeWidgetDataChange(widgetState, data, defaultWidgetState);
}

export function makeSpecificSaveableWidgetDataMergeFunction<
  T extends BaseSaveableWidgetState<any, any>,
>(
  defaultWidgetState: T,
  getIsDirty?: (widgetState: T) => boolean,
  resetErrorStateOnChange?: boolean
) {
  return (widgetState: T, data: Partial<T['currentData']>) =>
    mergeSaveableWidgetDataChange(
      widgetState,
      data,
      defaultWidgetState,
      getIsDirty,
      resetErrorStateOnChange
    );
}

export function createNetworkRequestActionSet<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>(
  reduxKey: string,
  actionType: string,
  options: {
    successPayloadGenerator?: (
      data: PuppyApiResponseWrapper<ResponseDataType>
    ) => SuccessPayload;
  } = {}
): {
  submit: ActionFunction0<any>;
  success: SuccessAction<ResponseDataType, SuccessPayload>;
  failure: FailureAction<RequestDataType>;
} {
  return {
    submit: createAction(`${reduxKey}.${actionType}`, () => ({})),
    success: createAction(
      `${reduxKey}.${actionType}_SUCCESS`,
      (data: PuppyApiResponseWrapper<ResponseDataType>) => ({
        ...(options.successPayloadGenerator
          ? options.successPayloadGenerator(data)
          : ({} as SuccessPayload)),
      })
    ),
    failure: createAction(
      `${reduxKey}.${actionType}_FAILURE`,
      (errorMessage: string, errors: ErrorDetails<RequestDataType>) => ({
        errorMessage,
        errors,
      })
    ),
  };
}

/**
 * In certain cases, the data for the api request is not stored in redux. This function handles that case by allowing the data to be supplied on the consumer's side.
 */
export function createNetworkRequestActionSetWithSuppliedData<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>(
  reduxKey: string,
  actionType: string,
  options: {
    successPayloadGenerator?: (
      data: PuppyApiResponseWrapper<ResponseDataType>
    ) => SuccessPayload;
  } = {}
) {
  const { failure, success } = createNetworkRequestActionSet<
    RequestDataType,
    ResponseDataType,
    SuccessPayload
  >(reduxKey, actionType, options);

  return {
    submit: createAction(
      `${reduxKey}.${actionType}`,
      (data: RequestDataType) => ({ data })
    ),
    success,
    failure,
  };
}

export function networkRequestMetaReducer<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
  WidgetState = {},
>({
  initialAction,
  successAction,
  failureAction,
  additionalStateUpdates = {},
  requestDataPlacer,
}: {
  initialAction: ActionFunction1<RequestDataType, any> | ActionFunction0<any>;
  successAction: SuccessAction<ResponseDataType, SuccessPayload>;
  failureAction: FailureAction<RequestDataType>;
  additionalStateUpdates?: {
    initialStateUpdate?: (
      state: WidgetState,
      action: Action<RequestDataType>
    ) => Partial<WidgetState>;
    successStateUpdate?: (
      state: WidgetState,
      action: Action<SuccessPayload>
    ) => Partial<WidgetState>;
    failureStateUpdate?: (
      state: WidgetState,
      action: Action<ErrorDetails<RequestDataType>>
    ) => Partial<WidgetState>;
  };
  /**
   * Directs data to be placed in a different location in the state than the default (default function places it in the `request` field of the widget state).
   */
  requestDataPlacer?: (
    state: WidgetState,
    requestMeta: RequestMeta<RequestDataType>
  ) => Partial<WidgetState>;
}) {
  const additionalInitialStateUpdate =
    additionalStateUpdates.initialStateUpdate ?? (() => ({}));
  const additionalSuccessStateUpdate =
    additionalStateUpdates.successStateUpdate ?? (() => ({}));
  const additionalFailureStateUpdate =
    additionalStateUpdates.failureStateUpdate ?? (() => ({}));
  const requestDataPlacerFunction =
    requestDataPlacer ??
    ((state, data) => ({
      request: {
        ...data,
      },
    }));

  return {
    [initialAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE,
        errors: {},
      }),
      ...additionalInitialStateUpdate(state, action),
    }),
    [successAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.SUCCESS,
        errors: {},
      }),
      ...additionalSuccessStateUpdate(state, action),
    }),
    [failureAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.FAILURE,
        errorMessage: action.payload.errorMessage,
        errors: action.payload.errors,
      }),
      ...additionalFailureStateUpdate(state, action),
    }),
  };
}

export function saveableWidgetRequestMetaReducer<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>({
  initialAction,
  successAction,
  failureAction,
  clearDataOnSuccess = false,
}: {
  initialAction: ActionFunction0<any>;
  successAction: SuccessAction<ResponseDataType, SuccessPayload>;
  failureAction: FailureAction<RequestDataType>;
  clearDataOnSuccess?: boolean;
}): Record<
  string,
  (
    state: BaseSaveableWidgetState<any, any>,
    action: Action<any>
  ) => BaseSaveableWidgetState<any, any>
> {
  return {
    [initialAction.toString()]: (state, action) => ({
      ...state,
      saveRequest: {
        status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE,
        errors: {},
      },
    }),
    [successAction.toString()]: (state, action) => ({
      ...state,
      initialData: clearDataOnSuccess ? state.initialData : state.currentData,
      currentData: clearDataOnSuccess ? state.initialData : state.currentData,
      isDirty: false,
      saveRequest: {
        status: NetworkRequestState.SUCCESS,
        errors: {},
      },
    }),
    [failureAction.toString()]: (state, action) => ({
      ...state,
      saveRequest: {
        status: NetworkRequestState.FAILURE,
        errorMessage: action.payload.errorMessage,
        errors: action.payload.errors,
      },
    }),
  };
}

export function createErrorDetailsSelector<
  T extends BaseSaveableWidgetState<any, any>,
>(widgetStateSelector: OutputSelector<any, T, (a: any) => T>) {
  return createSelector(
    [widgetStateSelector],
    widgetState => widgetState.saveRequest.errors as T['saveRequest']['errors']
  );
}

export function* handleSaveResponse<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>(
  response: PuppyApiResponseWrapper<ResponseDataType>,
  successAction: SuccessAction<ResponseDataType, SuccessPayload>,
  failureAction: FailureAction<RequestDataType>,
  defaultErrorMessage: string,
  fieldErrorTranslationPrefix: string
) {
  if (!response || isApiError(response) || response.success === false) {
    const errorMessage =
      (response && response.errorDescription) || defaultErrorMessage;
    /**
     * The `generic` key isn't a field-specific error, so we'll store it and push a toast notification for it.
     */
    let genericError: string | undefined = undefined;
    const errors: ErrorDetails<RequestDataType> =
      response &&
      response.errorDetails &&
      typeof response.errorDetails === 'object' &&
      !Array.isArray(response.errorDetails)
        ? Object.entries(response.errorDetails).reduce((acc, [key, value]) => {
            if (key === 'generic') {
              genericError = i18next.t(
                `${fieldErrorTranslationPrefix}.${value}`
              );
              return acc;
            }

            // Because the backend request type has fields in snake_case, but the backend error response references those fields via camelCase,
            // we convert to snake_case. Otherwise, we'd have to drop support for field-related type hints.
            (acc as Record<string, string>)[snakeCase(key)] = i18next.t(
              `${fieldErrorTranslationPrefix}.${value}`
            );
            return acc;
          }, {} as ErrorDetails<RequestDataType>)
        : {};
    const hasFieldErrors = Object.keys(errors).length > 0;
    yield all([
      put(failureAction(errorMessage, errors)),
      genericError
        ? put(addErrorNotification(genericError))
        : !hasFieldErrors && put(addErrorNotification(errorMessage)),
    ]);
    return false;
  }

  yield put(successAction(response));
  return true;
}

/**
 * Provides standardized props for our input fields if an API error has occurred.
 *
 * This does not handle client-side validation errors.
 */
export function useInputFieldApiErrorProps<T extends RequestMeta<any>>(
  selector: OutputSelector<any, T, (a: any) => T>,
  apiFieldKey: keyof T['errors']
):
  | {
      details: React.ReactNode;
      'aria-errormessage': string;
      validationResult: 'success' | 'error';
    }
  | {} {
  const { status, errors } = useSelector(selector);
  const shouldShowFieldError =
    status === NetworkRequestState.FAILURE && !!errors[apiFieldKey];
  const errorMessageId = `${String(apiFieldKey)}-error`;
  return shouldShowFieldError
    ? {
        details: (
          <InputErrorMessage id={errorMessageId}>
            {errors[apiFieldKey]}
          </InputErrorMessage>
        ),
        'aria-errormessage': shouldShowFieldError ? errorMessageId : undefined,
        validationResult: shouldShowFieldError ? 'error' : undefined,
      }
    : {};
}
